// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getFirestore, doc, setDoc, getDoc, collection, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, limit, onSnapshot, or, serverTimestamp, arrayUnion, increment, runTransaction, startAfter, startAt } from "firebase/firestore";
import { getAuth, GoogleAuthProvider, signInWithRedirect, getRedirectResult, signInWithPopup, signInWithPhoneNumber, RecaptchaVerifier, setPersistence, browserLocalPersistence } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { withRetry, executeWithRetry } from "../utils/retryLogic.js";
// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
// Loaded from environment variables for security
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Validate Firebase configuration
const validateFirebaseConfig = () => {
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'appId'];
  for (const field of requiredFields) {
    if (!firebaseConfig[field]) {
      console.error(`❌ Firebase config missing: ${field}`);
      return false;
    }
  }

  console.log("============================================");
  console.log("🔥 Firebase Configuration:");
  console.log("============================================");
  console.log("Project ID:", firebaseConfig.projectId);
  console.log("Auth Domain:", firebaseConfig.authDomain);
  console.log("API Key:", firebaseConfig.apiKey ? 'Present' : 'MISSING');
  console.log("App ID:", firebaseConfig.appId);
  console.log("============================================");

  return true;
};

// Initialize Firebase with comprehensive error handling
let app, analytics, db, auth, googleProvider, storage;

try {
  // Validate config first
  if (validateFirebaseConfig()) {
    console.log("Initializing Firebase with config:", firebaseConfig);

    // Initialize Firebase app
    app = initializeApp(firebaseConfig);

    // Initialize services with proper error handling
    try {
      analytics = getAnalytics(app);
      console.log("Analytics initialized");
    } catch (error) {
      console.warn("Analytics initialization failed:", error);
      analytics = null;
    }

    try {
      db = getFirestore(app);
      console.log("Firestore initialized");
    } catch (error) {
      console.error("Firestore initialization failed:", error);
      db = null;
    }

    try {
      auth = getAuth(app);
      console.log("Auth initialized");

      // Configure Auth settings
      auth.languageCode = 'es';
      auth.useDeviceLanguage();

      // Configure session persistence to survive page refreshes
      setPersistence(auth, browserLocalPersistence)
        .then(() => {
          console.log("✅ Session persistence configured: browserLocalPersistence");
        })
        .catch((error) => {
          console.warn("⚠️ Failed to set persistence:", error);
        });

      // Configure Google Provider with proper settings
      googleProvider = new GoogleAuthProvider();

      // NOTA: Simplificamos los parámetros para evitar problemas con OAuth
      // El parámetro 'access_type=offline' puede causar problemas en algunos casos
      googleProvider.setCustomParameters({
        'prompt': 'select_account'
      });

      // Scopes básicos necesarios
      googleProvider.addScope('email');
      googleProvider.addScope('profile');

      console.log("✅ Google provider configured with scopes:", googleProvider.scopes);

      // Development settings
      if (import.meta.env.DEV) {
        auth.settings = {
          appVerificationDisabledForTesting: true
        };
        console.log("Development mode - app verification disabled");
      }

      console.log("Google provider configured successfully");

    } catch (error) {
      console.error("Auth initialization failed:", error);
      auth = null;
      googleProvider = null;
    }

    // Initialize Storage
  storage = null;
  try {
    storage = getStorage(app);
  } catch (error) {
    console.error("Storage initialization failed:", error);
  }

  // Make Firebase available globally for debugging
    if (typeof window !== 'undefined') {
      window.firebaseApp = app;
      window.firebaseAuth = auth;
      window.firebaseDB = db;
      window.firebaseStorage = storage;
      console.log("✅ Firebase instances attached to window for debugging");

      // Check current auth state immediately
      if (auth) {
        setTimeout(() => {
          const currentUser = auth.currentUser;
          console.log("============================================");
          console.log("🔍 Current Auth State at Initialization:");
          console.log("============================================");
          console.log("Has User:", !!currentUser);
          if (currentUser) {
            console.log("User Email:", currentUser.email);
            console.log("User UID:", currentUser.uid);
            console.log("Email Verified:", currentUser.emailVerified);
          } else {
            console.log("No user currently authenticated");
          }
          console.log("============================================");
        }, 500);
      }
    }

  } else {
    throw new Error("Invalid Firebase configuration");
  }
} catch (error) {
  console.error("Fatal error initializing Firebase:", error);

  // Create fallback objects to prevent crashes
  app = null;
  analytics = null;
  db = null;
  auth = null;
  googleProvider = null;
  storage = null;

  // Show user-friendly error
  if (typeof window !== 'undefined') {
    window.FIREBASE_ERROR = error.message;
  }
}

// Function to sign in with Google using POPUP (better for development, avoids SW issues)
export const signInWithGooglePopup = async () => {
  try {
    if (!auth || !googleProvider) {
      throw new Error("Firebase Auth not initialized");
    }

    console.log("============================================");
    console.log("🚀 Initiating Google OAuth Popup");
    console.log("============================================");

    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    console.log("✅ User signed in successfully with popup:", user.email);
    console.log("👤 User UID:", user.uid);
    console.log("🔑 Credential:", result.credential ? 'Present' : 'Missing');

    return result;
  } catch (error) {
    console.error("❌ Error initiating Google popup:", error);
    console.error("❌ Error code:", error.code);
    console.error("❌ Error message:", error.message);

    // User closed the popup
    if (error.code === 'auth/popup-closed-by-user') {
      console.log("ℹ️ User closed the popup");
    }
    throw error;
  }
};

// Function to sign in with Google using redirect (avoids COOP issues)
export const signInWithGoogleRedirect = async () => {
  try {
    if (!auth || !googleProvider) {
      throw new Error("Firebase Auth not initialized");
    }

    console.log("============================================");
    console.log("🚀 Initiating Google OAuth Redirect");
    console.log("============================================");
    console.log("Auth Domain:", firebaseConfig.authDomain);
    console.log("Current URL:", window.location.href);
    console.log("Redirect URL will be:", `${firebaseConfig.authDomain}/__/auth/handler`);
    console.log("============================================");

    await signInWithRedirect(auth, googleProvider);
    console.log("✅ Redirect initiated - page will redirect to Google");
    return null; // Result will be handled by getRedirectResult
  } catch (error) {
    console.error("❌ Error initiating Google redirect:", error);
    console.error("❌ Error code:", error.code);
    console.error("❌ Error message:", error.message);
    throw error;
  }
};

// Function to get the redirect result after Google sign-in
export const getGoogleRedirectResult = async () => {
  try {
    if (!auth) {
      throw new Error("Firebase Auth not initialized");
    }

    console.log('🔍 Calling getRedirectResult...');
    console.log('📍 Current auth state:', auth ? 'Auth initialized' : 'Auth NOT initialized');
    console.log('🌐 Current URL:', window.location.href);

    const result = await getRedirectResult(auth);
    if (result) {
      console.log("✅ User signed in successfully with redirect:", result.user.email);
      console.log("👤 User UID:", result.user.uid);
      console.log("🔑 Credential:", result.credential ? 'Present' : 'Missing');
      return result;
    } else {
      console.log("⚠️ No redirect result available (normal for non-redirect flows)");
    }
    return null;
  } catch (error) {
    console.error("❌ Error getting Google redirect result:", error);
    console.error("❌ Error code:", error.code);
    console.error("❌ Error message:", error.message);
    throw error;
  }
};

// Function to register/save user after successful Google sign-in
export const registerUser = async (user) => {
  try {
    if (!db) {
      throw new Error("Firestore not initialized");
    }

    // Save user data to Firestore
    const userData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
      provider: 'google.com',
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
      // Add any additional fields you need
    };

    await setDoc(doc(db, "users", user.uid), userData, { merge: true });
    console.log("User registered successfully:", user.uid);
    return userData;
  } catch (error) {
    console.error("Error registering user:", error);
    throw error;
  }
};

// ========== DATABASE FUNCTIONS ==========

// Function to save/update user data in Firestore with retry logic
export const saveUserData = withRetry(async (userId, userData) => {
  if (!db) {
    throw new Error("Firestore not initialized");
  }

  const userRef = doc(db, "users", userId);
  const dataToSave = {
    ...userData,
    updatedAt: new Date().toISOString()
  };

  await setDoc(userRef, dataToSave, { merge: true });
  console.log("User data saved successfully for userId:", userId);
  return true;
}, {
  maxRetries: 3,
  baseDelay: 500, // Shorter delay for user data updates
  retryableErrors: ['unavailable', 'deadline-exceeded', 'network-request-failed']
});

// Function to read user data from Firestore with retry logic
export const getUserData = withRetry(async (userId) => {
  if (!db) {
    throw new Error("Firestore not initialized");
  }

  const userRef = doc(db, "users", userId);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    console.log("User data found for userId:", userId);
    return userDoc.data();
  } else {
    console.log("No user data found for userId:", userId);
    return null;
  }
}, {
  maxRetries: 2,
  baseDelay: 300,
  retryableErrors: ['unavailable', 'deadline-exceeded', 'network-request-failed']
});

// General function to write data to any collection
export const writeData = async (collectionName, documentId, data, options = {}) => {
  try {
    if (!db) {
      throw new Error("Firestore not initialized");
    }

    const docRef = doc(db, collectionName, documentId);
    const dataToSave = {
      ...data,
      updatedAt: new Date().toISOString()
    };

    // Si no se especifica merge, agregar createdAt solo en nuevos documentos
    if (!options.merge) {
      dataToSave.createdAt = new Date().toISOString();
    }

    await setDoc(docRef, dataToSave, { merge: options.merge || false });
    console.log(`Data written successfully to ${collectionName}/${documentId}`);
    return true;
  } catch (error) {
    console.error("Error writing data:", error);
    throw error;
  }
};

// Function to add a new document with auto-generated ID
export const addDocument = async (collectionName, data) => {
  try {
    if (!db) {
      throw new Error("Firestore not initialized");
    }

    const colRef = collection(db, collectionName);
    const dataToSave = {
      ...data,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const docRef = await addDoc(colRef, dataToSave);
    console.log(`Document added successfully to ${collectionName} with ID:`, docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("Error adding document:", error);
    throw error;
  }
};

// General function to read data from any collection
export const readData = async (collectionName, documentId) => {
  try {
    if (!db) {
      throw new Error("Firestore not initialized");
    }

    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      console.log(`Data found in ${collectionName}/${documentId}`);
      return docSnap.data();
    } else {
      console.log(`No data found in ${collectionName}/${documentId}`);
      return null;
    }
  } catch (error) {
    console.error("Error reading data:", error);
    throw error;
  }
};

// Function to get all documents from a collection
export const getAllDocuments = async (collectionName, orderByField = 'createdAt', orderDirection = 'desc') => {
  try {
    if (!db) {
      throw new Error("Firestore not initialized");
    }

    const colRef = collection(db, collectionName);
    const q = query(colRef, orderBy(orderByField, orderDirection));
    const querySnapshot = await getDocs(q);

    const documents = [];
    querySnapshot.forEach((doc) => {
      documents.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log(`Retrieved ${documents.length} documents from ${collectionName}`);
    return documents;
  } catch (error) {
    console.error("Error getting all documents:", error);
    // Si el error es porque la colección no existe o está vacía, devolver array vacío
    if (error.code === 'not-found' || error.message?.includes('index')) {
      console.log(`Collection ${collectionName} does not exist or has no documents, returning empty array`);
      return [];
    }
    throw error;
  }
};

// Function to get a single document by ID
export const getEventById = async (eventId) => {
  try {
    if (!db) {
      throw new Error("Firestore not initialized");
    }

    const eventRef = doc(db, "events", eventId);
    const eventDoc = await getDoc(eventRef);

    if (eventDoc.exists()) {
      console.log(`Event found: ${eventId}`);
      return {
        id: eventDoc.id,
        ...eventDoc.data()
      };
    } else {
      console.log(`No event found with ID: ${eventId}`);
      return null;
    }
  } catch (error) {
    console.error("Error getting event by ID:", error);
    throw error;
  }
};

// Function to delete expired events (older than 1 day)
export const cleanExpiredEvents = async () => {
  try {
    if (!db) {
      throw new Error("Firestore not initialized");
    }

    // Calculate cutoff date (current date - 1 day)
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 1);
    const cutoffTimestamp = Math.floor(cutoffDate.getTime() / 1000);

    console.log(`Cleaning events older than: ${cutoffDate.toISOString()}`);

    // Query for events older than cutoff date
    const eventsRef = collection(db, 'events');
    const q = query(eventsRef, where('createdAt', '<', cutoffTimestamp));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log('No expired events found to clean');
      return 0;
    }

    // Delete all expired events in a batch
    const batch = writeBatch(db);
    snapshot.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    console.log(`✅ Cleaned ${snapshot.size} expired events`);
    return snapshot.size;
  } catch (error) {
    console.error('Error cleaning expired events:', error);
    throw error;
  }
};

// Function to query documents with specific conditions
export const queryDocuments = async (collectionName, conditions) => {
  try {
    if (!db) {
      throw new Error("Firestore not initialized");
    }

    const colRef = collection(db, collectionName);
    let q = colRef;

    // Apply conditions
    conditions.forEach(condition => {
      const { field, operator, value } = condition;
      q = query(q, where(field, operator, value));
    });

    const querySnapshot = await getDocs(q);
    const documents = [];

    querySnapshot.forEach((doc) => {
      documents.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log(`Retrieved ${documents.length} documents from ${collectionName} with conditions`);
    return documents;
  } catch (error) {
    console.error("Error querying documents:", error);
    throw error;
  }
};

// Function to update a document
export const updateDocument = async (collectionName, documentId, updateData) => {
  try {
    if (!db) {
      throw new Error("Firestore not initialized");
    }

    const docRef = doc(db, collectionName, documentId);
    const dataToUpdate = {
      ...updateData,
      updatedAt: new Date().toISOString()
    };

    await updateDoc(docRef, dataToUpdate);
    console.log(`Document updated successfully in ${collectionName}/${documentId}`);
    return true;
  } catch (error) {
    console.error("Error updating document:", error);
    throw error;
  }
};

// Function to delete a document
export const deleteDocument = async (collectionName, documentId) => {
  try {
    if (!db) {
      throw new Error("Firestore not initialized");
    }

    const docRef = doc(db, collectionName, documentId);
    await deleteDoc(docRef);
    console.log(`Document deleted successfully from ${collectionName}/${documentId}`);
    return true;
  } catch (error) {
    console.error("Error deleting document:", error);
    throw error;
  }
};

// Helper function to detect and handle ad-blocker issues
export const checkFirebaseConnection = () => {
  const isBlocked = () => {
    // Check if common ad-blocker patterns are detected
    const blockedIndicators = [
      'ERR_BLOCKED_BY_CLIENT',
      'net::ERR_BLOCKED_BY_CLIENT',
      'Failed to load resource: net::ERR_BLOCKED_BY_CLIENT'
    ];

    return blockedIndicators.some(indicator =>
      console.error.toString().includes(indicator)
    );
  };

  if (isBlocked()) {
    console.warn('🚫 Firebase connection blocked by ad-blocker or privacy extension');
    return {
      isBlocked: true,
      message: 'Tu ad-blocker está bloqueando Firebase. Desactívalo temporalmente o añade firebaseio.com a tu lista blanca.'
    };
  }

  return {
    isBlocked: false,
    message: 'Firebase connection OK'
  };
};

// Notification functions
export const createNotification = async (notificationData, user) => {
  try {
    if (!user) {
      throw new Error('User must be authenticated to create notifications');
    }

    const notification = {
      ...notificationData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return await addDocument('notifications', notification);
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

export const updateNotification = async (notificationId, updateData, user) => {
  try {
    if (!user) {
      throw new Error('User must be authenticated to update notifications');
    }

    const updatedData = {
      ...updateData,
      updatedAt: new Date().toISOString(),
    };

    return await updateDocument('notifications', notificationId, updatedData);
  } catch (error) {
    console.error('Error updating notification:', error);
    throw error;
  }
};

export const deleteNotification = async (notificationId, user) => {
  try {
    if (!user) {
      throw new Error('User must be authenticated to delete notifications');
    }

    return await deleteDocument('notifications', notificationId);
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

export const getUserNotifications = async (userId, read = null) => {
  try {
    let conditions = [
      { field: 'userId', operator: '==', value: userId }
    ];

    // Filter by read status if specified
    if (read !== null) {
      conditions.push({ field: 'read', operator: '==', value: read });
    }

    return await queryDocuments('notifications', conditions);
  } catch (error) {
    console.error('Error getting user notifications:', error);
    throw error;
  }
};

export const subscribeToUserNotifications = (userId, callback, options = {}) => {
  try {
    const limit = options.limit || 20 // Por defecto 20 notificaciones para optimizar costos
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limit)
    );

    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
      }));
      callback(notifications);
    }, (error) => {
      console.error('Error subscribing to notifications:', error);
    });
  } catch (error) {
    console.error('Error setting up notification subscription:', error);
    throw error;
  }
};

// User management functions for admin panel
export const getAllUsers = async (maxResults = 100) => {
  try {
    const q = query(
      collection(db, 'users'),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      uid: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
      lastLoginAt: doc.data().lastLoginAt?.toDate?.() || null,
    }));
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

export const searchUsers = async (searchTerm) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      or(
        where('displayName', '>=', searchTerm),
        where('displayName', '<=', searchTerm + '\uf8ff'),
        where('email', '>=', searchTerm),
        where('email', '<=', searchTerm + '\uf8ff')
      )
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      uid: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
    }));
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};

export const updateUserRole = async (userId, role, updatedBy) => {
  try {
    const updateData = {
      role,
      updatedAt: new Date().toISOString(),
      updatedBy: updatedBy?.uid || null,
      roleUpdatedAt: new Date().toISOString(),
    };

    return await updateDocument('users', userId, updateData);
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

export const getUserStats = async () => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);

    const users = snapshot.docs.map(doc => doc.data());
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    return {
      total: users.length,
      activeLastWeek: users.filter(user =>
        user.lastLoginAt && new Date(user.lastLoginAt) > oneWeekAgo
      ).length,
      activeLastMonth: users.filter(user =>
        user.lastLoginAt && new Date(user.lastLoginAt) > oneMonthAgo
      ).length,
      newToday: users.filter(user =>
        user.createdAt && new Date(user.createdAt).toDateString() === now.toDateString()
      ).length,
      newThisWeek: users.filter(user =>
        user.createdAt && new Date(user.createdAt) > oneWeekAgo
      ).length,
    };
  } catch (error) {
    console.error('Error getting user stats:', error);
    throw error;
  }
};

// ============= EVENT VOTING FUNCTIONS =============

/**
 * Vote for an event (up or down)
 * @param {string} eventId - Event ID from eventos_estructurados.json
 * @param {string} userId - User ID from authentication
 * @param {string} vote - 'up' or 'down'
 * @returns {Promise<Object>} Result with success status and message
 */
export const voteEvent = withRetry(async (eventId, userId, vote) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!eventId || !userId || !vote) {
    throw new Error('eventId, userId, and vote are required');
  }
  if (!['up', 'down'].includes(vote)) {
    throw new Error('vote must be either "up" or "down"');
  }

  try {
    // Check if user already voted for this event
    const voteDocRef = doc(db, 'eventVotes', `${eventId}_${userId}`);
    const existingVote = await getDoc(voteDocRef);

    if (existingVote.exists()) {
      const currentVote = existingVote.data().vote;

      // If same vote, remove it (toggle off)
      if (currentVote === vote) {
        await deleteDoc(voteDocRef);
        return { success: true, message: 'Vote removed', action: 'removed' };
      }

      // If different vote, update it
      await updateDoc(voteDocRef, {
        vote,
        updatedAt: serverTimestamp()
      });
      return { success: true, message: 'Vote updated', action: 'updated' };
    }

    // Create new vote
    await setDoc(voteDocRef, {
      eventId,
      userId,
      vote,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { success: true, message: 'Vote recorded', action: 'created' };
  } catch (error) {
    console.error('Error voting for event:', error);
    throw error;
  }
});

/**
 * Get vote counts for an event (up and down)
 * @param {string} eventId - Event ID
 * @returns {Promise<Object>} Object with upCount and downCount
 */
export const getEventVotes = withRetry(async (eventId) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!eventId) throw new Error('eventId is required');

  try {
    const votesQuery = query(
      collection(db, 'eventVotes'),
      where('eventId', '==', eventId)
    );

    const querySnapshot = await getDocs(votesQuery);

    let upCount = 0;
    let downCount = 0;

    querySnapshot.forEach((doc) => {
      const vote = doc.data().vote;
      if (vote === 'up') upCount++;
      else if (vote === 'down') downCount++;
    });

    return { upCount, downCount, total: upCount + downCount };
  } catch (error) {
    // Silently handle permission errors - return default values instead of throwing
    if (error.code === 'permission-denied' ||
        error.message?.includes('Missing or insufficient permissions')) {
      return { upCount: 0, downCount: 0, total: 0 };
    }
    console.error('Error getting event votes:', error);
    throw error;
  }
});

/**
 * Get a specific user's vote for an event
 * @param {string} eventId - Event ID
 * @param {string} userId - User ID
 * @returns {Promise<string|null>} 'up', 'down', or null if no vote
 */
export const getUserEventVote = withRetry(async (eventId, userId) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!eventId || !userId) {
    throw new Error('eventId and userId are required');
  }

  try {
    const voteDocRef = doc(db, 'eventVotes', `${eventId}_${userId}`);
    const voteDoc = await getDoc(voteDocRef);

    if (voteDoc.exists()) {
      return voteDoc.data().vote;
    }

    return null;
  } catch (error) {
    // Silently handle permission errors - don't log them
    if (error.code === 'permission-denied' ||
        error.message?.includes('Missing or insufficient permissions')) {
      return null;
    }
    console.error('Error getting user vote:', error);
    throw error;
  }
});

/**
 * Subscribe to vote count changes for an event (real-time)
 * @param {string} eventId - Event ID
 * @param {Function} callback - Callback function with vote data
 * @returns {Function} Unsubscribe function
 */
export const subscribeToEventVotes = (eventId, callback) => {
  if (!db) {
    console.error('Firestore not initialized');
    return () => {};
  }

  try {
    const votesQuery = query(
      collection(db, 'eventVotes'),
      where('eventId', '==', eventId)
    );

    let hasUnsubscribed = false;

    const unsubscribe = onSnapshot(votesQuery, (querySnapshot) => {
      // Prevent callbacks after unsubscribe
      if (hasUnsubscribed) return;

      let upCount = 0;
      let downCount = 0;

      querySnapshot.forEach((doc) => {
        const vote = doc.data().vote;
        if (vote === 'up') upCount++;
        else if (vote === 'down') downCount++;
      });

      // Double-check before callback
      if (!hasUnsubscribed) {
        callback({ upCount, downCount, total: upCount + downCount });
      }
    }, (error) => {
      // Only log error if not intentionally unsubscribed AND not a permission error
      if (!hasUnsubscribed) {
        // Silently handle permission errors
        if (error.code !== 'permission-denied' &&
            !error.message?.includes('Missing or insufficient permissions')) {
          console.error('Error subscribing to event votes:', error);
        }
        callback({ upCount: 0, downCount: 0, total: 0 });
      }
    });

    return () => {
      hasUnsubscribed = true;
      unsubscribe();
    };
  } catch (error) {
    // Silently handle permission errors in setup
    if (error.code !== 'permission-denied' &&
        !error.message?.includes('Missing or insufficient permissions')) {
      console.error('Error setting up vote subscription:', error);
    }
    return () => {};
  }
};

/**
 * Vote on an event (up or down)
 * @param {string} eventId - Event ID
 * @param {string} voteType - 'up' or 'down'
 * @param {string} userId - User ID (optional, will use current auth user if not provided)
 * @returns {Promise<boolean>} Success status
 */
export const voteOnEvent = withRetry(async (eventId, voteType, userId) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!eventId) throw new Error('eventId is required');
  if (voteType !== 'up' && voteType !== 'down') {
    throw new Error('voteType must be "up" or "down"');
  }

  // Get current user if userId not provided
  let currentUserId = userId;
  if (!currentUserId && auth?.currentUser) {
    currentUserId = auth.currentUser.uid;
  }

  if (!currentUserId) {
    throw new Error('User must be authenticated to vote');
  }

  try {
    const voteDocRef = doc(db, 'eventVotes', `${eventId}_${currentUserId}`);

    // Check if user already voted
    const existingVote = await getDoc(voteDocRef);

    if (existingVote.exists()) {
      const currentVote = existingVote.data().vote;

      // If same vote, remove it (toggle)
      if (currentVote === voteType) {
        await deleteDoc(voteDocRef);
        return true;
      }

      // If different vote, update it
      await updateDoc(voteDocRef, {
        vote: voteType,
        updatedAt: serverTimestamp()
      });
      return true;
    }

    // Create new vote
    await setDoc(voteDocRef, {
      eventId,
      userId: currentUserId,
      vote: voteType,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    // Handle permission errors
    if (error.code === 'permission-denied' ||
        error.message?.includes('Missing or insufficient permissions')) {
      console.error('Permission denied for event voting');
      return false;
    }
    console.error('Error voting on event:', error);
    throw error;
  }
});

// ============= GROUP VOTING FUNCTIONS =============

/**
 * Vote for a group (up or down)
 * @param {string} groupId - Group ID (slug)
 * @param {string} userId - User ID from authentication
 * @param {string} vote - 'up' or 'down'
 * @returns {Promise<Object>} Result with success status and message
 */
export const voteGroup = withRetry(async (groupId, userId, vote) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!groupId || !userId || !vote) {
    throw new Error('groupId, userId, and vote are required');
  }
  if (!['up', 'down'].includes(vote)) {
    throw new Error('vote must be either "up" or "down"');
  }

  try {
    // Check if user already voted for this group
    const voteDocRef = doc(db, 'groupVotes', `${groupId}_${userId}`);
    const existingVote = await getDoc(voteDocRef);

    if (existingVote.exists()) {
      const currentVote = existingVote.data().vote;

      // If same vote, remove it (toggle off)
      if (currentVote === vote) {
        await deleteDoc(voteDocRef);
        return { success: true, message: 'Vote removed', action: 'removed' };
      }

      // If different vote, update it
      await updateDoc(voteDocRef, {
        vote,
        updatedAt: serverTimestamp()
      });
      return { success: true, message: 'Vote updated', action: 'updated' };
    }

    // Create new vote
    await setDoc(voteDocRef, {
      groupId,
      userId,
      vote,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { success: true, message: 'Vote recorded', action: 'created' };
  } catch (error) {
    console.error('Error voting for group:', error);
    throw error;
  }
});

/**
 * Get vote counts for a group (up and down)
 * @param {string} groupId - Group ID
 * @returns {Promise<Object>} Object with upCount and downCount
 */
export const getGroupVotes = withRetry(async (groupId) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!groupId) throw new Error('groupId is required');

  try {
    const votesQuery = query(
      collection(db, 'groupVotes'),
      where('groupId', '==', groupId)
    );

    const querySnapshot = await getDocs(votesQuery);

    let upCount = 0;
    let downCount = 0;

    querySnapshot.forEach((doc) => {
      const vote = doc.data().vote;
      if (vote === 'up') upCount++;
      else if (vote === 'down') downCount++;
    });

    return { upCount, downCount, total: upCount + downCount };
  } catch (error) {
    // Silently handle permission errors - return default values instead of throwing
    if (error.code === 'permission-denied' ||
        error.message?.includes('Missing or insufficient permissions')) {
      return { upCount: 0, downCount: 0, total: 0 };
    }
    console.error('Error getting group votes:', error);
    throw error;
  }
});

/**
 * Get a specific user's vote for a group
 * @param {string} groupId - Group ID
 * @param {string} userId - User ID
 * @returns {Promise<string|null>} 'up', 'down', or null if no vote
 */
export const getUserGroupVote = withRetry(async (groupId, userId) => {
  if (!db) throw new Error('Firestore not initialized');
  if (!groupId || !userId) {
    throw new Error('groupId and userId are required');
  }

  try {
    const voteDocRef = doc(db, 'groupVotes', `${groupId}_${userId}`);
    const voteDoc = await getDoc(voteDocRef);

    if (voteDoc.exists()) {
      return voteDoc.data().vote;
    }

    return null;
  } catch (error) {
    console.error('Error getting user group vote:', error);
    throw error;
  }
});

/**
 * Subscribe to vote count changes for a group (real-time)
 * @param {string} groupId - Group ID
 * @param {Function} callback - Callback function with vote data
 * @returns {Function} Unsubscribe function
 */
export const subscribeToGroupVotes = (groupId, callback) => {
  if (!db) {
    console.error('Firestore not initialized');
    return () => {};
  }

  try {
    const votesQuery = query(
      collection(db, 'groupVotes'),
      where('groupId', '==', groupId)
    );

    const unsubscribe = onSnapshot(votesQuery, (querySnapshot) => {
      let upCount = 0;
      let downCount = 0;

      querySnapshot.forEach((doc) => {
        const vote = doc.data().vote;
        if (vote === 'up') upCount++;
        else if (vote === 'down') downCount++;
      });

      callback({ upCount, downCount, total: upCount + downCount });
    }, (error) => {
      console.error('Error subscribing to group votes:', error);
      callback({ upCount: 0, downCount: 0, total: 0 });
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up group vote subscription:', error);
    return () => {};
  }
};

/**
 * Subscribe to all groups (real-time) con paginación optimizada para costos
 * @param {Function} callback - Callback function with { groups, lastDoc, hasMore }
 * @param {Object} options - Opciones { limit: number, includeInactive: boolean, startAfter: DocumentSnapshot }
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAllGroups = (callback, options = {}) => {
  if (!db) {
    console.error('Firestore not initialized');
    return () => {};
  }

  try {
    const queryLimit = options.limit || 100 // Por defecto cargar todos los grupos (máximo 100)
    const includeInactive = options.includeInactive || false // Por defecto excluir inactivos
    const startAfterDoc = options.startAfter || null // Para paginación

    console.log('🔍 subscribeToAllGroups: Setting up query...', { db: !!db, queryLimit, includeInactive, startAfter: !!startAfterDoc });

    // Construir query con o sin startAfter para paginación
    let groupsQuery = query(
      collection(db, 'groups'),
      limit(queryLimit)
    );

    if (startAfterDoc) {
      groupsQuery = query(
        collection(db, 'groups'),
        startAfter(startAfterDoc),
        limit(queryLimit)
      );
    }

    console.log('✅ subscribeToAllGroups: Query created successfully');

    const unsubscribe = onSnapshot(groupsQuery, (querySnapshot) => {
      const groups = [];
      let lastVisibleDoc = null; // Último documento devuelto por Firestore (para paginación)
      let lastValidDoc = null; // Último documento válido incluido (para continuar paginación correctamente)

      querySnapshot.forEach((doc) => {
        // Guardar el último documento visible para paginación
        lastVisibleDoc = doc;

        const groupData = {
          id: doc.id,
          ...doc.data()
        };
        // Filtrar grupos activos (excluir soft deletes) a menos que se pidan todos
        // También incluir grupos creados por el sistema (datos importados)
        const shouldInclude = includeInactive ||
          groupData.isActive !== false ||
          groupData.createdBy === 'system';

        if (shouldInclude) {
          groups.push(groupData);
          lastValidDoc = doc; // Guardar último documento válido para paginación
        }
      });

      // Para paginación, usar el último documento DEVUELTO por Firestore (no el último válido)
      // Esto evita saltarse documentos que están inactivos
      const lastDoc = lastVisibleDoc;

      // Determinar si hay más grupos:
      // - Si NO hay startAfter (primera carga), siempre hasMore=false porque cargamos todos
      // - Si hay startAfter, hasMore siempre es false porque no hay más después
      const hasMore = false;

      console.log(`📊 subscribeToAllGroups: Loaded ${groups.length} groups (docs=${querySnapshot.docs.length}, hasMore=${hasMore}, includeInactive=${includeInactive})`);

      // Retornar objeto con todos los grupos (sin paginación)
      callback({
        groups,
        lastDoc: null, // No hay paginación
        hasMore: false // Todos los grupos cargados de una vez
      });
    }, (error) => {
      console.error('Error subscribing to groups:', error);
      callback({ groups: [], lastDoc: null, hasMore: false });
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up groups subscription:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      stack: error.stack,
      name: error.name
    });
    return () => {};
  }
};

/**
 * Subscribe to all events (real-time) con paginación optimizada para costos
 * @param {Function} callback - Callback function with { events, lastDoc, hasMore }
 * @param {Object} options - Opciones { limit: number, includeInactive: boolean, startAfter: DocumentSnapshot }
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAllEvents = (callback, options = {}) => {
  if (!db) {
    console.error('Firestore not initialized');
    return () => {};
  }

  try {
    const queryLimit = options.limit || 10 // Por defecto 10 eventos + paginación
    const includeInactive = options.includeInactive || false // Por defecto excluir inactivos
    const startAfterDoc = options.startAfter || null // Para paginación

    // Construir query con o sin startAfter para paginación
    let eventsQuery = query(
      collection(db, 'events'),
      orderBy('createdAt', 'desc'),
      limit(queryLimit)
    );

    if (startAfterDoc) {
      eventsQuery = query(
        collection(db, 'events'),
        orderBy('createdAt', 'desc'),
        startAfter(startAfterDoc),
        limit(queryLimit)
      );
    }

    const unsubscribe = onSnapshot(eventsQuery, (querySnapshot) => {
      const events = [];
      let lastDoc = null;

      querySnapshot.forEach((doc) => {
        const eventData = { id: doc.id, ...doc.data() };
        // Filtrar eventos activos (excluir soft deletes) a menos que se pidan todos
        // También incluir eventos creados por el sistema (datos importados)
        const shouldInclude = includeInactive ||
          eventData.isActive !== false ||
          eventData.organizerId === 'system' ||
          eventData.source === 'system';

        if (shouldInclude) {
          events.push(eventData);
          lastDoc = doc; // Guardar último documento para paginación
        }
      });

      // Determinar si hay más eventos
      const hasMore = querySnapshot.docs.length === queryLimit;

      console.log(`📊 subscribeToAllEvents: Loaded ${events.length} events (hasMore=${hasMore}, includeInactive=${includeInactive})`);

      // Retornar objeto con eventos y metadata de paginación
      callback({
        events,
        lastDoc,
        hasMore
      });
    }, (error) => {
      console.error('Error subscribing to events:', error);
      callback({ events: [], lastDoc: null, hasMore: false });
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up events subscription:', error);
    return () => {};
  }
};

// ============= PHONE AUTHENTICATION FUNCTIONS =============

/**
 * Create a RecaptchaVerifier instance for phone authentication
 * @param {string} containerId - ID of the container element (or button ID for invisible)
 * @param {object} options - Configuration options
 * @returns {RecaptchaVerifier} RecaptchaVerifier instance
 */
export const createRecaptchaVerifier = (containerId, options = {}) => {
  if (!auth) {
    throw new Error("Firebase Auth not initialized");
  }

  const defaultOptions = {
    size: 'invisible', // 'invisible' or 'normal'
    callback: (response) => {
      console.log('reCAPTCHA solved:', response);
    },
    'expired-callback': () => {
      console.warn('reCAPTCHA expired');
    }
  };

  const mergedOptions = { ...defaultOptions, ...options };

  try {
    const recaptchaVerifier = new RecaptchaVerifier(auth, containerId, mergedOptions);
    console.log('reCAPTCHA verifier created for:', containerId);
    return recaptchaVerifier;
  } catch (error) {
    console.error('Error creating reCAPTCHA verifier:', error);
    throw error;
  }
};

/**
 * Send verification code to phone number
 * @param {string} phoneNumber - Phone number in format: +1234567890
 * @param {RecaptchaVerifier} appVerifier - RecaptchaVerifier instance
 * @returns {Promise<ConfirmationResult>} Confirmation result with confirm method
 */
export const sendPhoneVerificationCode = async (phoneNumber, appVerifier) => {
  if (!auth) {
    throw new Error("Firebase Auth not initialized");
  }

  if (!phoneNumber || !appVerifier) {
    throw new Error("Phone number and app verifier are required");
  }

  // Validate phone number format (should start with + and have 10-15 digits)
  const phoneRegex = /^\+\d{10,15}$/;
  if (!phoneRegex.test(phoneNumber)) {
    throw new Error("Invalid phone number format. Use format: +1234567890");
  }

  try {
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
    console.log('SMS verification code sent to:', phoneNumber);
    return confirmationResult;
  } catch (error) {
    console.error('Error sending verification code:', error);

    // Reset reCAPTCHA on error for retry
    if (window.recaptchaWidgetId) {
      window.grecaptcha?.reset(window.recaptchaWidgetId);
    }

    throw error;
  }
};

/**
 * Verify phone code and complete sign-in
 * @param {ConfirmationResult} confirmationResult - Result from sendPhoneVerificationCode
 * @param {string} verificationCode - 6-digit code from SMS
 * @returns {Promise<User>} Firebase user object
 */
export const verifyPhoneCode = async (confirmationResult, verificationCode) => {
  if (!confirmationResult || !verificationCode) {
    throw new Error("Confirmation result and verification code are required");
  }

  // Validate code format (6 digits)
  const codeRegex = /^\d{6}$/;
  if (!codeRegex.test(verificationCode)) {
    throw new Error("Invalid verification code format. Must be 6 digits");
  }

  try {
    const result = await confirmationResult.confirm(verificationCode);
    console.log('Phone verification successful for user:', result.user.uid);
    return result.user;
  } catch (error) {
    console.error('Error verifying phone code:', error);
    throw error;
  }
};

/**
 * Complete phone authentication and create user profile
 * @param {User} user - Firebase user from verification
 * @param {string} phoneNumber - Phone number used for authentication
 * @returns {Promise<object>} Created user profile
 */
export const completePhoneAuth = async (user, phoneNumber) => {
  if (!db) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Check if user exists in Firestore
    const userDoc = await getDoc(doc(db, "users", user.uid));

    if (!userDoc.exists()) {
      // Create new user profile for phone auth
      const userProfile = {
        uid: user.uid,
        phoneNumber: phoneNumber,
        displayName: `Usuario_${phoneNumber.slice(-4)}`, // Default name with last 4 digits
        photoURL: null,
        email: null,
        emailVerified: false,
        provider: 'phone',
        primaryRole: 10,
        roleName: 'USER',
        status: 'active',
        preferences: {
          notifications: true,
          theme: 'light',
          language: 'es'
        },
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString()
      };

      await setDoc(doc(db, "users", user.uid), userProfile, { merge: true });
      console.log('User profile created for phone auth:', user.uid);

      return userProfile;
    } else {
      // Update last login for existing user
      const existingData = userDoc.data();
      await updateDoc(doc(db, "users", user.uid), {
        lastLoginAt: new Date().toISOString(),
        phoneNumber: phoneNumber
      });

      return existingData;
    }
  } catch (error) {
    console.error('Error completing phone auth:', error);
    throw error;
  }
};

/**
 * Link phone number to existing account
 * @param {User} user - Current Firebase user
 * @param {string} phoneNumber - Phone number to link
 * @param {string} verificationCode - 6-digit code from SMS
 * @returns {Promise<User>} Updated user
 */
export const linkPhoneNumber = async (user, phoneNumber, verificationCode) => {
  if (!auth) {
    throw new Error("Firebase Auth not initialized");
  }

  try {
    // This would require PhoneAuthProvider.credential and linkWithCredential
    // For now, we'll log it as a future enhancement
    console.log('Link phone number feature - to be implemented');
    throw new Error('Link phone number feature not yet implemented');
  } catch (error) {
    console.error('Error linking phone number:', error);
    throw error;
  }
};

export {
  app,
  analytics,
  db,
  auth,
  storage,
  googleProvider,
  serverTimestamp,
  arrayUnion,
  increment,
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  or,
  runTransaction
};